/* eslint-disable react-hooks/exhaustive-deps */
import withRouter from "../_shared/withRouter";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { globalLoader, globalAlert } from "../actions/commonActions";
import {
  lmsGetCourseApi,
  lmsCourseReadinessApi,
  lmsUpdateCourseStatusApi,
  lmsCopyCourseApi,
  lmsCourseReportApi,
} from "./_lmsActions";
import {
  computeReadiness,
  distinctAssetFileName,
  parseCourseDetail,
} from "./_lmsHelpers";
import { exportData, logOut } from "../_shared/commonFunction";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import { StatusBadge } from "./lmsOverview";
import { formatDate } from "./lmsCourses";
import { Link } from "react-router-dom";

const TABS = [
  { key: "overview", icon: "fas fa-chart-pie", label: "Overview" },
  { key: "content", icon: "fas fa-layer-group", label: "Contents" },
  { key: "learners", icon: "fas fa-user-graduate", label: "Learners" },
];

const firstArray = (...values) =>
  values.find((value) => Array.isArray(value)) || [];

const moduleSections = (module) => module?.sections || [];
const unwrapAsset = (entry) => entry?.asset || entry;
const courseIntroAssets = (course) =>
  (course?.introAssets || []).map((item) => item?.asset || item);
const moduleAssets = (module) => [
  ...(module?.introductionAssets || []),
  ...(module?.completionAssets || []),
  ...(module?.supportServiceAssets || module?.supportServicesAssets || []),
  ...moduleSections(module).flatMap((section) => section?.assets || []),
].map(unwrapAsset);
const videoDurationSeconds = (assets = []) =>
  assets.reduce((sum, asset) => {
    if (asset?.type !== "video") return sum;
    return sum + (Number(asset?.durationSeconds) || 0);
  }, 0);
const moduleVideoDurationSeconds = (module) => videoDurationSeconds(moduleAssets(module));
const formatWatchTime = (seconds = 0) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  if (totalSeconds <= 0) return "0 sec";
  if (totalSeconds < 60) return `${totalSeconds} sec`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) {
    return `${totalMinutes} min${remainingSeconds ? ` ${remainingSeconds} sec` : ""}`;
  }
  return `${hours} ${hours === 1 ? "hr" : "hrs"}${minutes ? ` ${minutes} min` : ""}${remainingSeconds ? ` ${remainingSeconds} sec` : ""}`;
};
const learnerIdFromEnrolment = (enrolment) =>
  enrolment?.learner?._id || enrolment?.learner?.id || enrolment?.learner;
const learnerNameFromEnrolment = (enrolment) =>
  enrolment?.learner?.name ||
  `${enrolment?.learner?.firstName || ""} ${enrolment?.learner?.lastName || ""
    }`.trim() ||
  "-";
const enrolmentDistrict = (enrolment) =>
  enrolment?.learner?.districtRef?.name ||
  enrolment?.learner?.district?.name ||
  enrolment?.learner?.district ||
  "-";
const countCourseParts = (modules = []) =>
  modules.reduce((total, module) => total + moduleSections(module).length, 0);
const countCompletedParts = (enrolment) =>
  (enrolment?.moduleProgress || []).reduce(
    (total, moduleProgress) =>
      total +
      (moduleProgress?.sectionProgress || []).filter(
        (section) => section?.status === "completed" || section?.completedAt
      ).length,
    0
  );
const learnerOrganisation = (enrolment) =>
  enrolment?.learner?.learnerOrganisation || enrolment?.learner?.organisation || "-";
const learnerEmailFromEnrolment = (enrolment) => enrolment?.learner?.email || "-";
const getLearnerProgress = (enrolment, modules = []) => {
  const totalParts = countCourseParts(modules);
  const completedParts = countCompletedParts(enrolment);
  const progressPercent = totalParts
    ? Math.min(100, Math.round((completedParts / totalParts) * 100))
    : enrolment?.status === "completed"
      ? 100
      : 0;
  return { totalParts, completedParts, progressPercent };
};
const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (text.startsWith('="') && text.endsWith('"')) {
    return text;
  }
  return `"${text.replace(/"/g, '""')}"`;
};
const normalizeFilterText = (value) => String(value || "").trim().toLowerCase();
const contentNodeIcon = (node) => {
  if (node?.type === "course") return "fas fa-book-open";
  if (node?.type === "module") return "fas fa-folder";
  if (node?.type === "course_intro" || node?.type === "module_intro") return "fas fa-video";
  if (node?.type === "parts") return "fas fa-layer-group";
  if (node?.type === "part") return "fas fa-list-ul";
  if (node?.type === "quiz") return "fas fa-question-circle";
  if (node?.type === "completion") return "fas fa-flag-checkered";
  if (node?.type === "support") return "fas fa-life-ring";
  if (node?.assetType === "video") return "fas fa-video";
  if (node?.assetType === "pdf") return "fas fa-file-pdf";
  if (node?.assetType === "image") return "fas fa-image";
  return "fas fa-file-alt";
};
const contentAssetTypeLabel = (type) => {
  const normalized = String(type || "").toLowerCase();
  if (normalized === "pdf") return "PDF";
  if (normalized === "rich_text") return "Text";
  if (!normalized) return "";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};
const contentAssetLabel = (asset, labelPrefix = "Content") => {
  const typeLabel = contentAssetTypeLabel(asset?.type);
  const prefix = String(labelPrefix || "Content").trim();
  if (!typeLabel) return prefix;
  return typeLabel === "PDF" ? `${prefix} PDF` : `${prefix} ${typeLabel.toLowerCase()}`;
};
const assetFileName = (asset = {}) => {
  const explicitName = asset?.filename || asset?.fileName;
  if (explicitName) return String(explicitName);
  const source = asset?.title || asset?.url || "";
  const cleanSource = String(source).split("?")[0];
  const name = cleanSource.split("/").filter(Boolean).pop() || source;
  try {
    return decodeURIComponent(name);
  } catch (error) {
    return name;
  }
};
const contentNodeMeta = (node) =>
  [
    node?.moduleCount ? `${node.moduleCount} ${node.moduleCount === 1 ? "module" : "modules"}` : null,
    node?.sectionCount ? `${node.sectionCount} ${node.sectionCount === 1 ? "part" : "parts"}` : null,
    node?.count ? `${node.count} ${node.count === 1 ? "part" : "parts"}` : null,
    node?.serviceAudience,
    node?.serviceArea,
    contentAssetTypeLabel(node?.assetType),
    node?.durationSeconds ? formatWatchTime(node.durationSeconds) : null,
    node?.questionCount ? `${node.questionCount} questions` : null,
  ]
    .filter(Boolean)
    .join(" • ");
const contentNodeStatus = (node) => {
  const status = (node?.status || "").toLowerCase();
  if (!status) return null;
  return status;
};
const contentNodeDefaultLabel = (node) => {
  if (node?.type === "course") return "Course name";
  if (node?.type === "module") return "Module name";
  return "";
};
const fallbackContentIndex = (course, modules = []) => {
  const introChildren = (course?.introAssets || []).map((item, index) => ({
    id: item?._id || `${item?.serviceAudience || "intro"}-${index}`,
    type: "asset",
    assetType: item?.asset?.type,
    label: contentAssetLabel(item?.asset, `${item?.serviceAudience || "Course"} introduction`),
    title: item?.asset?.title || assetFileName(item?.asset) || `${item?.serviceAudience || "Course"} intro`,
    fileName: assetFileName(item?.asset),
    serviceAudience: item?.serviceAudience,
    durationSeconds: item?.asset?.durationSeconds || 0,
    status: item?.asset?.url || item?.asset?.content ? "ready" : "missing",
  }));
  return {
    summary: {
      modules: modules.length,
      parts: modules.reduce((sum, module) => sum + moduleSections(module).length, 0),
    },
    tree: [
      {
        id: "course-contents",
        type: "course",
        label: "Course name",
        title: course?.title || "Course contents",
        status: course?.status || "draft",
        moduleCount: modules.length,
        children: [
          {
            id: "course-intro",
            type: "course_intro",
            title: "Course introduction",
            status: introChildren.length ? "ready" : "missing",
            children: introChildren,
          },
          ...modules.map((module, moduleIndex) => {
            const sections = moduleSections(module);
            return {
              id: module?._id || module?.moduleId || `module-${moduleIndex}`,
              type: "module",
              label: "Module name",
              title: module?.title || `Module ${moduleIndex + 1}`,
              status: module?.isPublished ? "published" : "draft",
              sectionCount: sections.length,
              children: [
                {
                  id: `${module?._id || moduleIndex}-parts`,
                  type: "parts",
                  title: "Parts",
                  count: sections.length,
                  status: sections.length ? "ready" : "missing",
                  children: sections.map((section, sectionIndex) => ({
                    id: section?._id || `section-${moduleIndex}-${sectionIndex}`,
                    type: "part",
                    title: section?.title || `Part ${sectionIndex + 1}`,
                    status: (section?.assets || []).length ? "ready" : "missing",
                    children: [
                      ...(section?.assets || []).map((asset, assetIndex) => ({
                        id: asset?._id || `asset-${moduleIndex}-${sectionIndex}-${assetIndex}`,
                        type: "asset",
                        assetType: asset?.type,
                        label: contentAssetLabel(asset, assetIndex === 0 ? "Part" : `Part asset ${assetIndex + 1}`),
                        title: asset?.title || `Asset ${assetIndex + 1}`,
                        fileName: assetFileName(asset),
                        durationSeconds: asset?.durationSeconds || 0,
                        status: asset?.url || asset?.content ? "ready" : "missing",
                      })),
                      ...(section?.quizDoc || section?.quiz
                        ? [
                          {
                            id: section?.quizDoc?._id || `quiz-${moduleIndex}-${sectionIndex}`,
                            type: "quiz",
                            label: "Section quiz",
                            title: section?.quizDoc?.title || "Section quiz",
                            status: section?.quizDoc?.status || "draft",
                            questionCount: section?.quizDoc?.questions?.length || 0,
                          },
                        ]
                        : []),
                    ],
                  })),
                },
              ],
            };
          }),
        ],
      },
    ],
  };
};

const ContentTreeNode = ({ node, depth = 0 }) => {
  const children = node?.children || [];
  const status = contentNodeStatus(node);
  const canCollapse = ["course", "module", "parts", "part"].includes(node?.type) && children.length > 0;
  const [isExpanded, setIsExpanded] = useState(true);
  const label = node?.label || contentNodeDefaultLabel(node);
  const hasLabel = !!label;
  const value = hasLabel ? node?.title || "Untitled" : node?.title || "Untitled";
  const rawFileName =
    node?.type === "asset"
      ? node?.fileName || ""
      : "";
  const fileName = distinctAssetFileName(value, rawFileName);
  return (
    <div className={`lms-content-node depth-${Math.min(depth, 3)} type-${node?.type || "item"}`}>
      <div className="lms-content-node-main">
        {canCollapse ? (
          <button
            type="button"
            className="lms-content-collapse-btn"
            aria-label={isExpanded ? `Collapse ${node?.title || "item"}` : `Expand ${node?.title || "item"}`}
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((current) => !current)}
          >
            <i className={`fas ${isExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}></i>
          </button>
        ) : (
          <span className="lms-content-collapse-spacer"></span>
        )}
        <span className="lms-content-node-icon">
          <i className={contentNodeIcon(node)}></i>
        </span>
        <div className="lms-content-node-copy">
          {hasLabel && <span className="lms-content-node-label">{label}</span>}
          <strong>{value}</strong>
          {fileName && <em>{fileName}</em>}
          {contentNodeMeta(node) && <span>{contentNodeMeta(node)}</span>}
        </div>
        {status && (
          <span className={`lms-content-status status-${status}`}>
            {status}
          </span>
        )}
      </div>
      {!!children.length && (!canCollapse || isExpanded) && (
        <div className="lms-content-children">
          {children.map((child, index) => (
            <ContentTreeNode
              node={child}
              depth={depth + 1}
              key={child?.id || `${node?.id || "node"}-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LmsCourseDetail = (props) => {
  const { t } = useTranslation();
  const [allowView, setAllowView] = useState(false);
  const [course, setCourse] = useState(null);
  const [serverReadiness, setServerReadiness] = useState(null);
  const [navbarToggle, setNavbarToggle] = useState(false);
  const [modules, setModules] = useState([]);
  const [tab, setTab] = useState("overview");
  const [enrolments, setEnrolments] = useState([]);
  const [totalLearners, setTotalLearners] = useState(0);
  const [contentIndex, setContentIndex] = useState(null);
  const [learnerSearch, setLearnerSearch] = useState("");
  const [learnerDistrictFilter, setLearnerDistrictFilter] = useState("all");
  const [learnerProgressFilter, setLearnerProgressFilter] = useState("all");
  const [copyingCourse, setCopyingCourse] = useState(false);


  const courseIdentifier = props?.match?.params?.id;
  const courseRecordId = course?._id || course?.courseId || "";
  const courseSlug = course?.slug || courseIdentifier;
  const detailPath = `/admin/lms/course/detail/${courseSlug}`;
  const editPath = courseSlug ? `/admin/lms/course/${courseSlug}` : "";
  const previewPath = courseSlug ? `/admin/lms/course/${courseSlug}/preview` : "";


  useEffect(() => {
    loadCourse(true);
  }, [courseIdentifier]);

  const loadCourse = async (first = false) => {
    try {
      globalLoader(true);
      const res = await lmsGetCourseApi(courseIdentifier);
      if (res?.data?.status === 200) {
        const { course: c, modules: m, contentIndex: ci } = parseCourseDetail(res?.data?.data);
        setCourse(c);
        setModules(m || []);
        setContentIndex(ci || null);
        setAllowView(true);
        if (c?.slug && c.slug !== courseIdentifier) {
          props?.history?.replace(`/admin/lms/course/detail/${c.slug}`);
        }
        loadReadiness(c?._id || c?.courseId || courseIdentifier);
      } else {
        globalAlert("error", res?.data?.message);
      }
      globalLoader(false);
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message);
      if (error?.response?.data?.status === 401)
        logOut(props?.history, "/admin/login");
    }
  };
  const loadReadiness = async (identifier = courseRecordId || courseIdentifier) => {
    try {
      const res = await lmsCourseReadinessApi(identifier);
      if (res?.data?.status === 200)
        setServerReadiness(res?.data?.data?.readiness || res?.data?.data || null);
    } catch (e) {
      /* non-fatal */
    }
  };
  const readiness = course
    ? computeReadiness(course, modules, null, t)
    : { checks: [], score: 0 };
  const status = (course?.status || "draft").toLowerCase();
  const isReadOnlyArchivedCourse =
    status === "archived" && Number(course?.publishedVersion || 0) > 0;
  const canCopyCourse = status === "published" || isReadOnlyArchivedCourse;
  /* ── header actions ── */
  const setStatus = async (status) => {
    try {
      globalLoader(true);
      const res = await lmsUpdateCourseStatusApi(courseRecordId, status);
      if (res?.data?.status === 200) {
        globalAlert("success", res?.data?.message);
        loadCourse();
      } else {
        globalAlert("error", res?.data?.message);
        globalLoader(false);
      }
    } catch (error) {
      globalAlert("error", error?.response?.data?.message);
      globalLoader(false);
    }
  };
  const copyToNewCourse = async () => {
    if (!courseRecordId || copyingCourse) return;
    setCopyingCourse(true);
    try {
      globalLoader(true);
      const res = await lmsCopyCourseApi(courseRecordId);
      if (res?.data?.status === 200) {
        const copiedCourse = res?.data?.data?.course || {};
        const copiedIdentifier = copiedCourse?.slug || copiedCourse?._id || copiedCourse?.courseId;
        globalAlert("success", res?.data?.message || "Course copied to a new draft.");
        if (copiedIdentifier) {
          props?.history?.push(`/admin/lms/course/${copiedIdentifier}`);
        }
      } else {
        globalAlert("error", res?.data?.message);
      }
    } catch (error) {
      globalAlert("error", error?.response?.data?.message || "Unable to copy this course.");
    } finally {
      globalLoader(false);
      setCopyingCourse(false);
    }
  };
  const loadLearners = async () => {
    if (!courseRecordId) return;
    try {
      const res = await lmsCourseReportApi(courseRecordId, "?page=1&limit=1000");
      if (res?.data?.status === 200) {
        const data = res?.data?.data || {};
        const rows = firstArray(
          data?.list,
          data?.enrolments,
          data?.enrollments,
          data?.learners,
          data?.results
        );
        setEnrolments(rows);
        setTotalLearners(data?.pagination?.total ?? 0);
      } else {
        globalAlert("error", res?.data?.message);
      }
    } catch (e) {
      globalAlert("error", e?.response?.data?.message);
    }
  };
  useEffect(() => {
    if (!courseRecordId) return;
    if (tab === "overview" || tab === "learners" || tab === "reports") loadLearners();
  }, [tab, courseRecordId]);

  const learnerDistrictOptions = useMemo(
    () =>
      [...new Set(enrolments.map(enrolmentDistrict).filter((district) => district && district !== "-"))]
        .sort((a, b) => a.localeCompare(b)),
    [enrolments]
  );

  const filteredEnrolments = useMemo(() => {
    const search = normalizeFilterText(learnerSearch);
    return enrolments.filter((enrolment) => {
      const district = enrolmentDistrict(enrolment);
      const { progressPercent } = getLearnerProgress(enrolment, modules);
      const searchable = normalizeFilterText(
        [
          learnerNameFromEnrolment(enrolment),
          learnerEmailFromEnrolment(enrolment),
          learnerOrganisation(enrolment),
          district,
        ].join(" ")
      );
      const matchesSearch = !search || searchable.includes(search);
      const matchesDistrict =
        learnerDistrictFilter === "all" || district === learnerDistrictFilter;
      const matchesProgress =
        learnerProgressFilter === "all" ||
        (learnerProgressFilter === "not-started" && progressPercent === 0) ||
        (learnerProgressFilter === "in-progress" && progressPercent > 0 && progressPercent < 100) ||
        (learnerProgressFilter === "completed" && progressPercent === 100);
      return matchesSearch && matchesDistrict && matchesProgress;
    });
  }, [enrolments, learnerSearch, learnerDistrictFilter, learnerProgressFilter, modules]);

  const clearLearnerFilters = () => {
    setLearnerSearch("");
    setLearnerDistrictFilter("all");
    setLearnerProgressFilter("all");
  };

  const exportFilteredLearners = () => {
    if (!filteredEnrolments.length) {
      globalAlert("warning", "No learners available for the selected filters.");
      return;
    }
    const headers = [
      "Name",
      "Email",
      "Organisation",
      "District",
      "Completed parts",
      "Total parts",
      "Progress %",
      "Last activity",
    ];
    const rows = filteredEnrolments.map((enrolment) => {
      const { totalParts, completedParts, progressPercent } = getLearnerProgress(enrolment, modules);
      return [
        learnerNameFromEnrolment(enrolment),
        learnerEmailFromEnrolment(enrolment),
        learnerOrganisation(enrolment),
        enrolmentDistrict(enrolment),
        completedParts,
        totalParts,
        progressPercent,
        `="${formatDate(enrolment?.lastActivity || enrolment?.updatedAt || enrolment?.enrolledAt)}"`,
      ];
    });
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    exportData(
      `\uFEFF${csv}`,
      `${courseSlug || "course"}-learners-${new Date().getTime()}.csv`,
      "text/csv;charset=utf-8;"
    );
  };

  const localVideoSeconds =
    videoDurationSeconds(courseIntroAssets(course)) +
    modules.reduce((sum, module) => sum + moduleVideoDurationSeconds(module), 0);
  const totalVideoSeconds =
    serverReadiness?.metrics?.videoDurationSeconds ?? localVideoSeconds;
  const introAudiences = (course?.introAssets || [])
    .map((item) => item?.serviceAudience)
    .filter(Boolean);
  const audienceLabel =
    (course?.serviceAudiences || course?.audiences || introAudiences)
      ?.filter?.(Boolean)
      ?.join(", ") || "ESHS, SSHS";
  const completionRules = [
    course?.completionCriteria?.requireAllModules ? "All modules" : null,
    course?.completionCriteria?.requireSectionQuizPass
      ? "Section quizzes"
      : null,
  ].filter(Boolean);
  const summaryRows = [
    ["Audience", audienceLabel],
    ["Completion rule", completionRules.join(" + ") || "Course content"],
    ["Video duration", formatWatchTime(totalVideoSeconds)],
  ];

  const serverChecks = serverReadiness?.checks || {};
  const introHas = (audience) =>
    (course?.introAssets || []).some(
      (item) => item?.serviceAudience === audience
    );
  const readinessItems = [
    {
      key: "info",
      label: "Course information",
      ok: !!course?.title?.trim(),
      action: "Edit course info",
      onClick: () => editPath && props?.history?.push(editPath),
    },
    {
      key: "introVideos",
      label: "Introduction videos",
      ok:
        (serverChecks.introVideoESHS ?? introHas("ESHS")) &&
        (serverChecks.introVideoSSHS ?? introHas("SSHS")),
      action: "Add video",
      onClick: () => editPath && props?.history?.push(editPath),
    },
    {
      key: "content",
      label: "Course contents",
      ok:
        serverChecks.contentReady ??
        (modules.length > 0 && modules.every((module) => {
          const sections = moduleSections(module);
          return sections.length && sections.every((section) => (section?.assets || []).length);
        })),
      action: "Manage",
      onClick: () => setTab("content"),
    },
    ...(course?.hasQuizPerSection || course?.completionCriteria?.requireSectionQuizPass
      ? [
        {
          key: "sectionQuizzes",
          label: "Section quizzes",
          ok: serverChecks.sectionQuizzes ?? true,
          action: "Review quizzes",
          onClick: () => setTab("content"),
        },
      ]
      : []),
    {
      key: "supportAssets",
      label: "Support service PDFs",
      ok: serverChecks.supportAssets ?? modules.every((module) => (module?.supportServiceAssets || module?.supportServicesAssets || []).length > 0),
      optional: true,
      action: "Configure",
      onClick: () => setTab("content"),
    },
  ];
  const requiredReadiness = readinessItems.filter((item) => !item.optional);
  const readinessScore = requiredReadiness.length
    ? Math.round((requiredReadiness.filter((item) => item.ok).length / requiredReadiness.length) * 100)
    : readiness.score;
  const readinessState =
    status === "published"
      ? "Published"
      : readinessScore === 100
        ? "Ready to publish"
        : "Setup incomplete";
  const canPublish = status === "draft" && readinessScore === 100;
  const courseContentIndex = contentIndex || fallbackContentIndex(course, modules);
  const contentSummary = courseContentIndex?.summary || {};
  const contentTree = courseContentIndex?.tree || [];
  return (
    <>
      <Helmet>
        <title>{t("admin.lms.courseDetail.title")} - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      {allowView && (
        <div className="admin-portal">
          <Header roleType="admin" />
          <div className={`${navbarToggle ? "active" : ""} wrapper`}>
            <SidebarComponent
              navbarToggle={navbarToggle}
              roleType="admin"
              menu="lms"
              subMenu="lms-courses"
            />
            <div className="main">
              <div className="main-wrapper lms-course-detail-shell">
                <div>
                  <p className="admin-banner">{t("admin.lms.courseDetail.title")}</p>
                </div>
                <SidebarArrow handleSidebarToggle={setNavbarToggle} />

                <div className="container-fluid lms-course-detail-container">
                  <div className="lms-breadcrumb mb-3">
                    <Link to="/admin/lms/courses">
                      {t("admin.lms.courses.title")}
                    </Link>{" "}
                    <i className="fas fa-chevron-right mx-1"></i>{" "}
                    {course?.title}
                  </div>

                  {/* Workspace header */}
                  <div className="lms-workspace-header lms-detail-header">
                    <div className="lms-workspace-title">
                      <h2>{course?.title}</h2>
                      <div className="lms-workspace-meta">
                        <StatusBadge status={course?.status} t={t} />
                        <span>
                          {t("admin.lms.workspace.updated")}:{" "}
                          {formatDate(course?.updatedAt)}
                        </span>
                        <span>
                          {t("common.created")}: {formatDate(course?.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="lms-actions">
                      <button
                        className="btn secondary-btn btn-own-grey btn-with-icon lms-preview-action"
                        onClick={() =>
                          props?.history?.push({
                            pathname: previewPath,
                            search: `?returnTo=${encodeURIComponent(
                              detailPath
                            )}`,
                            state: {
                              returnTo: detailPath,
                            },
                          })
                        }
                        disabled={!previewPath}
                      >
                        <i className="fas fa-eye mr-1"></i>
                        {t("admin.lms.preview.preview")}
                      </button>
                      {!isReadOnlyArchivedCourse && (
                        <button
                          className="btn secondary-btn btn-with-icon lms-edit-action"
                          onClick={() => editPath && props?.history?.push(editPath)}
                          disabled={!editPath}
                        >
                          <i className="fas fa-pen me-2"></i>
                          Edit course
                        </button>
                      )}
                      {canPublish && (
                        <button
                          type="button"
                          className="btn secondary-btn btn-with-icon lms-publish-action"
                          onClick={() => setStatus("published")}
                        >
                          <i className="fas fa-rocket mr-1"></i>
                          {t("admin.lms.wizard.publishNow")}
                        </button>
                      )}
                      {canCopyCourse && (
                        <button
                          type="button"
                          className="btn secondary-btn btn-own-grey btn-with-icon"
                          onClick={copyToNewCourse}
                          disabled={copyingCourse}
                        >
                          <i className="fas fa-copy mr-1"></i>
                          {copyingCourse ? "Copying..." : "Copy to New Course"}
                        </button>
                      )}
                      {status !== "archived" && (
                        <button
                          type="button"
                          className="btn secondary-btn btn-own-grey btn-with-icon lms-archive-action"
                          onClick={() => setStatus("archived")}
                        >
                          <i className="fas fa-archive"></i>
                          {t("admin.lms.workspace.archive")}
                        </button>
                      )}
                      {status === "archived" && !isReadOnlyArchivedCourse && (
                        <button
                          className="btn secondary-btn"
                          onClick={() => setStatus("draft")}
                        >
                          {t("admin.lms.workspace.unarchive")}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="lms-workspace-tabs btn-with-icon">
                    {TABS.map((tb) => (
                      <button
                        key={tb.key}
                        className={`lms-tab ${tab === tb.key ? "active" : ""}`}
                        onClick={() => setTab(tb.key)}
                      >
                        <i className={`${tb.icon} mr-1`}></i>
                        {tb.label || t(`admin.lms.workspace.tab_${tb.key}`)}
                      </button>
                    ))}
                  </div>

                  <div className="lms-workspace-body">
                    {/* Overview */}
                    {tab === "overview" && (
                      <div className="lms-detail-overview">
                        <div className="lms-overview-grid">
                          <div className="lms-overview-main">
                            <div className="card lms-form-card">
                              <div className="card-body">
                                <div className="lms-card-head">
                                  <h3 className="lms-section-title mb-0">
                                    <i className="fas fa-file-alt me-2"></i>
                                    Course summary
                                  </h3>
                                  {!isReadOnlyArchivedCourse && (
                                    <button
                                      type="button"
                                      className="btn btn-sm secondary-btn btn-own-grey btn-with-icon"
                                      onClick={() =>
                                        editPath && props?.history?.push(editPath)
                                      }
                                      disabled={!editPath}
                                    >
                                      <i className="fas fa-pen"></i>
                                      Edit details
                                    </button>
                                  )}
                                </div>
                                <div className="lms-course-description">
                                  <span>Description</span>
                                  <p>
                                    {course?.description ||
                                      t("common.noDataAvailable")}
                                  </p>
                                </div>
                                <div className="lms-summary-table">
                                  {summaryRows.map(([label, value]) => (
                                    <div className="lms-summary-row" key={label}>
                                      <span>{label}</span>
                                      <strong>{value}</strong>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                          </div>

                          <aside className="card lms-form-card lms-readiness-card">
                            <div className="card-body">
                              <div className="lms-card-head">
                                <h3 className="lms-section-title mb-0">
                                  <i className="fas fa-clipboard-check me-2"></i>
                                  Publishing readiness
                                </h3>
                                <span
                                  className={`lms-badge ${readinessScore === 100
                                    ? "badge-green"
                                    : "badge-amber"
                                    }`}
                                >
                                  {readinessState}
                                </span>
                              </div>
                              <div className="lms-readiness-list">
                                {readinessItems.map((item) => (
                                  <div
                                    className={`lms-readiness-item ${item.ok ? "is-ok" : "is-missing"
                                      }`}
                                    key={item.key}
                                  >
                                    <i
                                      className={`fas ${item.ok
                                        ? "fa-check-circle"
                                        : "fa-exclamation-circle"
                                        }`}
                                    ></i>
                                    <div>
                                      <strong>{item.label}</strong>
                                      {item.optional && (
                                        <small>Optional for this course</small>
                                      )}
                                    </div>
                                    {!item.ok && !isReadOnlyArchivedCourse && (
                                      <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={item.onClick}
                                      >
                                        {item.action} &rarr;
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </aside>
                        </div>
                      </div>
                    )}

                    {/* Contents */}
                    {tab === "content" && (
                      <div className="card lms-form-card">
                        <div className="card-body">
                          <div className="lms-card-head">
                            <h3 className="lms-section-title mb-0">
                              <i className="fas fa-layer-group me-2"></i>
                              Course contents
                            </h3>
                            {!isReadOnlyArchivedCourse && (
                              <button
                                type="button"
                                className="btn secondary-btn btn-with-icon"
                                onClick={() =>
                                  editPath && props?.history?.push(editPath)
                                }
                                disabled={!editPath}
                              >
                                <i className="fas fa-edit"></i>
                                Manage contents
                              </button>
                            )}
                          </div>
                          <div className="lms-content-summary">
                            <span><strong>{contentSummary.modules || 0}</strong> Modules</span>
                            <span><strong>{contentSummary.parts || 0}</strong> Parts</span>
                            <span><strong>{contentSummary.assets || 0}</strong> Assets</span>
                            <span><strong>{contentSummary.quizzes || 0}</strong> Quizzes</span>
                            <span><strong>{formatWatchTime(contentSummary.videoDurationSeconds || 0)}</strong> Video</span>
                          </div>
                          <div className="lms-content-tree">
                            {contentTree.length ? (
                              contentTree.map((node, index) => (
                                <ContentTreeNode
                                  node={node}
                                  key={node?.id || index}
                                />
                              ))
                            ) : (
                              <div className="lms-empty-inline">
                                No modules yet.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Learners */}
                    {tab === "learners" && (
                      <div className="lms-course-learners-card">
                        <div className="lms-course-learners-head">
                          <div>
                            <h3>Learners</h3>
                            <span>
                              Showing {filteredEnrolments.length} of {totalLearners || enrolments.length} staff
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn secondary-btn btn-with-icon lms-course-export-btn"
                            onClick={exportFilteredLearners}
                          >
                            <i className="fas fa-file-csv"></i>
                            Export CSV
                          </button>
                        </div>
                        <div className="lms-course-learner-filters d-flex align-items-center flex-wrap flex-md-nowrap" style={{ gap: '15px' }}>
                          <div className="lms-course-learner-search" style={{ flex: '1 1 40%' }}>
                            <i className="fas fa-search"></i>
                            <input
                              className="form-control"
                              value={learnerSearch}
                              placeholder="Search name, email, organisation, district..."
                              onChange={(event) => setLearnerSearch(event.target.value)}
                            />
                          </div>
                          <select
                            className="form-control"
                            value={learnerDistrictFilter}
                            onChange={(event) => setLearnerDistrictFilter(event.target.value)}
                            style={{ flex: '1 1 22%' }}
                          >
                            <option value="all">All districts</option>
                            {learnerDistrictOptions.map((district) => (
                              <option key={district} value={district}>
                                {district}
                              </option>
                            ))}
                          </select>
                          <select
                            className="form-control"
                            value={learnerProgressFilter}
                            onChange={(event) => setLearnerProgressFilter(event.target.value)}
                            style={{ flex: '1 1 22%' }}
                          >
                            <option value="all">All progress</option>
                            <option value="not-started">Not started</option>
                            <option value="in-progress">In progress</option>
                            <option value="completed">Completed</option>
                          </select>
                          <button
                            type="button"
                            className="btn lms-filter-reset"
                            onClick={clearLearnerFilters}
                            style={{ flex: '0 0 auto', whiteSpace: 'nowrap', width: 'auto' }}
                          >
                            <i className="fas fa-undo-alt" style={{ marginRight: '8px' }}></i>
                            Clear
                          </button>
                        </div>
                        <div className="table-responsive">
                          <table className="table lms-course-learners-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Organisation</th>
                                <th>District</th>
                                <th>Progress (parts)</th>
                                <th>Last activity</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredEnrolments.length ? (
                                filteredEnrolments.map((enrolment) => {
                                  const { totalParts, completedParts, progressPercent } = getLearnerProgress(enrolment, modules);
                                  const learnerId = learnerIdFromEnrolment(enrolment);
                                  return (
                                    <tr key={enrolment?._id || learnerId}>
                                      <td>
                                        <div className="lms-course-learner-name">
                                          <strong>{learnerNameFromEnrolment(enrolment)}</strong>
                                          <span>{enrolment?.learner?.email || "-"}</span>
                                        </div>
                                      </td>
                                      <td>{learnerOrganisation(enrolment)}</td>
                                      <td>{enrolmentDistrict(enrolment)}</td>
                                      <td>
                                        <div className="lms-course-progress-cell">
                                          <div className="lms-course-progress-track">
                                            <span style={{ width: `${progressPercent}%` }}></span>
                                          </div>
                                          <small>
                                            {totalParts
                                              ? `${completedParts}/${totalParts} parts complete`
                                              : t("common.noDataAvailable")}
                                          </small>
                                        </div>
                                      </td>
                                      <td>{formatDate(enrolment?.lastActivity || enrolment?.updatedAt || enrolment?.enrolledAt)}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-sm lms-course-detail-custom-view-btn"
                                          style={{ backgroundColor: '#322F91', color: 'white', borderRadius: '20px', padding: '4px 16px' }}
                                          onClick={() =>
                                            learnerId
                                              ? props?.history?.push(`/admin/lms/learners/${learnerId}`)
                                              : globalAlert("error", "Learner profile not available.")
                                          }
                                        >
                                          View
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={6} className="text-center">
                                    {t("common.noDataAvailable")}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(LmsCourseDetail);
